import React, { useState, useEffect } from 'react';

/**
 * AccessibilityWidget - WCAG 2.2 AA Compliant Accessibility Settings
 * Designed to match CustomCalendar.jsx styling
 */
const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 'medium',
    highContrast: false,
    dyslexicFont: false,
    theme: 'dark',
    reduceMotion: false,
  });

  // Load settings from localStorage on mount ONLY
  useEffect(() => {
    const savedSettings = localStorage.getItem('dizee-accessibility-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
        applySettings(parsed);
      } catch (e) {
        console.error('Failed to parse accessibility settings:', e);
      }
    }
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      const updatedSettings = { ...settings, reduceMotion: true };
      setSettings(updatedSettings);
      applySettings(updatedSettings);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySettings = (newSettings) => {
    const root = document.documentElement;
    const body = document.body;
    
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
      xlarge: '20px',
    };
    
    const scaleMap = {
      small: '0.875',
      medium: '1',
      large: '1.125',
      xlarge: '1.25',
    };
    
    root.style.setProperty('--base-font-size', fontSizeMap[newSettings.fontSize]);
    root.style.fontSize = fontSizeMap[newSettings.fontSize];
    
    // Apply zoom to body for site-wide scaling
    body.style.zoom = scaleMap[newSettings.fontSize];
    
    // Add viewport constraint to keep accessibility controls visible when zoomed
    const viewportStyle = document.getElementById('viewport-constraint-style') || document.createElement('style');
    viewportStyle.id = 'viewport-constraint-style';
    viewportStyle.textContent = `
      /* Keep accessibility button visible - inverse scale to counteract zoom */
      button[aria-label="Open accessibility settings"] {
        transform: scale(${1 / parseFloat(scaleMap[newSettings.fontSize])}) !important;
        transform-origin: bottom right !important;
        bottom: ${16 * parseFloat(scaleMap[newSettings.fontSize])}px !important;
        right: ${16 * parseFloat(scaleMap[newSettings.fontSize])}px !important;
        position: fixed !important;
        z-index: 99999 !important;
      }
      
      /* Keep accessibility panel visible */
      div[role="dialog"][aria-labelledby="accessibility-title"] {
        transform: scale(${1 / parseFloat(scaleMap[newSettings.fontSize])}) !important;
        transform-origin: bottom right !important;
        bottom: ${80 * parseFloat(scaleMap[newSettings.fontSize])}px !important;
        right: ${16 * parseFloat(scaleMap[newSettings.fontSize])}px !important;
        position: fixed !important;
        z-index: 99999 !important;
      }
    `;
    document.head.appendChild(viewportStyle);

    if (newSettings.highContrast) {
      root.setAttribute('data-high-contrast', 'true');
    } else {
      root.removeAttribute('data-high-contrast');
    }

    if (newSettings.dyslexicFont) {
      // Apply local OpenDyslexic font globally with maximum specificity for mobile and desktop
      const fontStyle = document.getElementById('dyslexic-font-style') || document.createElement('style');
      fontStyle.id = 'dyslexic-font-style';
      fontStyle.textContent = `
        /* Base rule for all elements */
        * {
          font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
        }
        
        /* Target pseudo-elements */
        *::before, *::after {
          font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
        }
        
        /* Override styled-components and inline styles */
        [style*="font-family"] {
          font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
        }
        
        /* Target common structural elements explicitly */
        html, body, div, span, p, a, button, input, textarea, select, label,
        h1, h2, h3, h4, h5, h6, ul, ol, li, table, td, th, tr, nav, section, article, aside, header, footer {
          font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
        }
      `;
      document.head.appendChild(fontStyle);
    } else {
      // Remove dyslexic font style
      const fontStyle = document.getElementById('dyslexic-font-style');
      if (fontStyle) fontStyle.remove();
      root.style.fontFamily = '';
    }

    // THEME: Use filters (perfect color inversion) + aggressive overflow protection
    
    if (newSettings.theme === 'light') {
      root.setAttribute('data-theme', 'light');
      body.style.backgroundColor = '#ffffff';
      body.style.color = '#000000';
      
      // Apply filter for full color inversion (like before)
      body.style.filter = 'invert(1) hue-rotate(180deg)';
      
      // AGGRESSIVE overflow protection - inject CSS that persists
      const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
      style.id = 'theme-scroll-fix';
      style.textContent = `
        /* Re-invert images so they appear normal */
        img:not([alt*="DIZEE"]), video, canvas {
          filter: invert(1) hue-rotate(180deg) !important;
        }
        
        /* Re-invert accessibility widget */
        button[aria-label="Open accessibility settings"],
        div[role="dialog"][aria-labelledby="accessibility-title"] {
          filter: invert(1) hue-rotate(180deg) !important;
        }
        
        /* CRITICAL: Body/html should NEVER scroll - only mobile viewport scrolls */
        /* This matches dark mode behavior perfectly */
        html, html[data-theme="light"], html[data-theme="grayscale"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        body, body[data-theme="light"], body[data-theme="grayscale"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        #root {
          overflow: visible !important;
          height: 100% !important;
        }
      `;
      document.head.appendChild(style);
      
    } else if (newSettings.theme === 'grayscale') {
      root.setAttribute('data-theme', 'grayscale');
      body.style.backgroundColor = '#000000';
      body.style.color = '#ffffff';
      
      // Apply grayscale filter (like before)
      body.style.filter = 'grayscale(100%)';
      
      // Same aggressive overflow protection
      const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
      style.id = 'theme-scroll-fix';
      style.textContent = `
        /* Body/html should NEVER scroll - only mobile viewport scrolls */
        html, html[data-theme="grayscale"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        body, body[data-theme="grayscale"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        #root {
          overflow: visible !important;
          height: 100% !important;
        }
      `;
      document.head.appendChild(style);
      
    } else {
      root.setAttribute('data-theme', 'dark');
      body.style.backgroundColor = '#000000';
      body.style.color = '#ffffff';
      body.style.filter = 'none';
      
      // Apply same overflow protection for dark mode (consistency across all themes)
      const style = document.getElementById('theme-scroll-fix') || document.createElement('style');
      style.id = 'theme-scroll-fix';
      style.textContent = `
        /* Body/html should NEVER scroll - only mobile viewport scrolls */
        html, html[data-theme="dark"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        body, body[data-theme="dark"] {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: relative !important;
          height: 100% !important;
        }
        
        #root {
          overflow: visible !important;
          height: 100% !important;
        }
      `;
      document.head.appendChild(style);
    }

    if (newSettings.reduceMotion) {
      root.style.setProperty('--animation-duration', '0.01ms');
      root.style.setProperty('--transition-duration', '0.01ms');
      root.setAttribute('data-reduce-motion', 'true');
      
      // Add comprehensive motion reduction
      const motionStyle = document.getElementById('reduce-motion-style') || document.createElement('style');
      motionStyle.id = 'reduce-motion-style';
      motionStyle.textContent = `
        /* Disable all animations and transitions */
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
        
        /* Disable auto-playing videos */
        video {
          animation-play-state: paused !important;
        }
        
        /* Disable parallax effects */
        [style*="transform"] {
          transform: none !important;
        }
        
        /* Stop GIF animations */
        img[src*=".gif"] {
          animation: none !important;
        }
      `;
      document.head.appendChild(motionStyle);
    } else {
      root.style.removeProperty('--animation-duration');
      root.style.removeProperty('--transition-duration');
      root.removeAttribute('data-reduce-motion');
      
      // Remove motion reduction style
      const motionStyle = document.getElementById('reduce-motion-style');
      if (motionStyle) motionStyle.remove();
    }
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem('dizee-accessibility-settings', JSON.stringify(newSettings));
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 'medium',
      highContrast: false,
      dyslexicFont: false,
      theme: 'dark',
      reduceMotion: false,
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.removeItem('dizee-accessibility-settings');
    // Reset zoom
    document.body.style.zoom = '1';
    // Remove viewport constraint style
    const viewportStyle = document.getElementById('viewport-constraint-style');
    if (viewportStyle) viewportStyle.remove();
    // Remove reduce motion style
    const motionStyle = document.getElementById('reduce-motion-style');
    if (motionStyle) motionStyle.remove();
    // Restore scroll lock for medium size
    const scrollFixStyle = document.getElementById('no-scrollbar-fix') || document.createElement('style');
    scrollFixStyle.id = 'no-scrollbar-fix';
    scrollFixStyle.textContent = `
      html, body {
        overflow: hidden !important;
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        position: relative !important;
      }
    `;
    document.head.appendChild(scrollFixStyle);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Floating accessibility button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-[9998] bg-black border border-[rgba(255,255,255,0.1)] p-3 rounded-lg transition-all hover:bg-[rgba(255,255,255,0.1)] hover:border-[rgba(59,130,246,0.5)] focus:outline-none focus:border-[#3b82f6]"
        aria-label="Open accessibility settings"
        aria-expanded={isOpen}
        title="Accessibility Settings"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Accessibility settings panel */}
      {isOpen && (
        <>
          <button 
            className="fixed inset-0 z-[9997] bg-transparent border-0 cursor-default" 
            onClick={() => setIsOpen(false)}
            aria-label="Close accessibility settings"
            tabIndex={-1}
          />
          
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-labelledby="accessibility-title"
            aria-modal="true"
            className="fixed bottom-20 right-4 z-[9999] w-80 bg-black border border-[rgba(255,255,255,0.2)] rounded-lg shadow-2xl"
            onKeyDown={handleKeyDown}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.1)]">
              <h2 id="accessibility-title" className="text-white text-[12px] font-medium">
                Accessibility Settings
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
                aria-label="Close accessibility settings"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Settings content */}
            <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
              {/* Font Size */}
              <div>
                <label className="block text-white text-[11px] font-medium mb-2">Font Size</label>
                <div className="grid grid-cols-4 gap-2">
                  {['small', 'medium', 'large', 'xlarge'].map((size) => (
                    <button
                      key={size}
                      onClick={() => updateSetting('fontSize', size)}
                      className={`px-2 py-2 text-[10px] rounded border transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6] ${
                        settings.fontSize === size
                          ? 'bg-[#3b82f6] text-white border-[#3b82f6]'
                          : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)]'
                      }`}
                    >
                      {size === 'xlarge' ? 'XL' : size.charAt(0).toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* High Contrast */}
              <button
                onClick={() => updateSetting('highContrast', !settings.highContrast)}
                className={`w-full flex items-center justify-between p-3 rounded border transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6] ${
                  settings.highContrast
                    ? 'bg-[rgba(59,130,246,0.2)] border-[#3b82f6]'
                    : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)]'
                }`}
              >
                <span className="text-white text-[11px] font-medium">High Contrast</span>
                <span className="text-[10px] text-[rgba(255,255,255,0.7)]">{settings.highContrast ? 'ON' : 'OFF'}</span>
              </button>

              {/* Dyslexic Font */}
              <button
                onClick={() => updateSetting('dyslexicFont', !settings.dyslexicFont)}
                className={`w-full flex items-center justify-between p-3 rounded border transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6] ${
                  settings.dyslexicFont
                    ? 'bg-[rgba(59,130,246,0.2)] border-[#3b82f6]'
                    : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)]'
                }`}
              >
                <span className="text-white text-[11px] font-medium">Dyslexia Font</span>
                <span className="text-[10px] text-[rgba(255,255,255,0.7)]">{settings.dyslexicFont ? 'ON' : 'OFF'}</span>
              </button>

              {/* Theme */}
              <div>
                <label className="block text-white text-[11px] font-medium mb-2">Theme</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'light', label: 'Light' },
                    { value: 'dark', label: 'Dark' },
                    { value: 'grayscale', label: 'Gray' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => updateSetting('theme', value)}
                      className={`px-2 py-2 text-[10px] rounded border transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6] ${
                        settings.theme === value
                          ? 'bg-[#3b82f6] text-white border-[#3b82f6]'
                          : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reduce Motion */}
              <button
                onClick={() => updateSetting('reduceMotion', !settings.reduceMotion)}
                className={`w-full flex items-center justify-between p-3 rounded border transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6] ${
                  settings.reduceMotion
                    ? 'bg-[rgba(59,130,246,0.2)] border-[#3b82f6]'
                    : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)]'
                }`}
              >
                <span className="text-white text-[11px] font-medium">Reduce Motion</span>
                <span className="text-[10px] text-[rgba(255,255,255,0.7)]">{settings.reduceMotion ? 'ON' : 'OFF'}</span>
              </button>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[rgba(255,255,255,0.1)]">
              <button
                onClick={resetSettings}
                className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[11px] rounded hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)] transition-all focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
              >
                Reset to Default
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
